// @ts-nocheck
import { isEmpty, uniq } from 'lodash';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { checkUserResourcePermissions } from 'sonata-static-storage/lib/helpers/user.helpers';

import {
  campaignsHaveDriveToStore,
  campaignsHaveLegacyFootfallAttribution,
  getVariableFilterParsed
} from '@/helpers/reporting.helpers';
import { reportingSetStatsGroups } from '@/store/actions/reporting.actions';
import { getBaseCurrencyName } from '@/store/selectors/baseCurrency.selectors';
import {
  getReportingStatsGroups,
  getSectionsVisibility,
  getSelectedCampaigns,
  getStaticMode,
  includeCustomEvents
} from '@/store/selectors/reporting.selectors';
import {
  getCartographyCountries,
  getCartographyLevel,
  getCartographyLevelIds,
  getCurrency,
  getTimeFrame,
  getTimeZone,
  getVariableType,
  getVariableValues
} from '@/store/selectors/reportingAdvancedFilter.selectors';
import { getBaseCurrencyCatalog, getUserRoles } from '@/store/selectors/resources.selectors';
import { getCurrentSession } from '@/store/selectors/session.selectors';
import { getCountry, getCurrentUser } from '@/store/selectors/user.selectors';
import StatsTable from '@/ts-modules/reporting/StatsTablesSection/StatsTable/StatsTable';

import type { StaticReports } from '@/ts-types/reporting.types';

type TableType = 'footfallAttribution' | 'stats' | 'viewability';

const TABLE_TYPES: Record<TableType, TableType> = {
  footfallAttribution: 'footfallAttribution',
  stats: 'stats',
  viewability: 'viewability'
};

type Props = {
  hasFootfall: boolean;
  staticReports: StaticReports;
  statsTableColumns: Record<TableType, Record<string, any>[]>;
};

function StatsTablesSection({ hasFootfall, staticReports, statsTableColumns }: Props) {
  const dispatch = useDispatch();
  const baseCurrency = useSelector(getBaseCurrencyName);
  const currentCurrency = useSelector(getCurrency);
  const country = useSelector(getCountry);
  const sectionsVisibility = useSelector(getSectionsVisibility);
  const session = useSelector(getCurrentSession);
  const user = useSelector(getCurrentUser);
  const userRoles = useSelector(getUserRoles);
  const reportingCampaigns: Record<string, any>[] = useSelector(getSelectedCampaigns);
  const baseCurrencyCatalog = useSelector(getBaseCurrencyCatalog);
  const staticMode = useSelector(getStaticMode);
  const { beginDate, endDate } = useSelector(getTimeFrame);
  const timezone = useSelector(getTimeZone);
  const generalCartographyCountries = useSelector(getCartographyCountries);
  const generalCartographyLevel = useSelector(getCartographyLevel);
  const generalCartographyLevelIds = useSelector(getCartographyLevelIds);
  const hasCustomEvents = useSelector(includeCustomEvents);
  const reportGroups = useSelector(getReportingStatsGroups);
  const isMixedAttribution =
    campaignsHaveDriveToStore(reportingCampaigns) && campaignsHaveLegacyFootfallAttribution(reportingCampaigns);
  const footfallAttributionDefaultGroups = isMixedAttribution
    ? uniq([...reportGroups.footfallAttribution, 'cid'])
    : reportGroups.footfallAttribution;

  const currencyInfo = baseCurrencyCatalog.find((c: { baseCurrency: string }) => c.baseCurrency === baseCurrency);
  const hasIncludeFADwellTimeKpis =
    reportingCampaigns.filter(campaign => campaign.hasNewAttributionStats).length > 0 &&
    (checkUserResourcePermissions('reportingFootfallAttribution', user, userRoles) ||
      checkUserResourcePermissions('reportingTotalFootfallAttribution', user, userRoles));
  const hasMinimumViewabilityStats =
    reportingCampaigns.filter(campaign => campaign.hasViewability).length > 0 &&
    checkUserResourcePermissions('reportingIasViewabilityEvents', user, userRoles);
  const variableType = useSelector(getVariableType);
  const variableValues = useSelector(getVariableValues);
  const variableFilter =
    variableType && variableValues.length > 0 ? getVariableFilterParsed(variableType, variableValues) : undefined;

  const cartography = generalCartographyCountries.length
    ? generalCartographyLevel && generalCartographyLevelIds.length
      ? { [generalCartographyLevel]: generalCartographyLevelIds }
      : { level0: generalCartographyCountries }
    : {};

  if (!currencyInfo) {
    return null;
  }

  const currency = {
    base: currencyInfo.baseCurrency,
    current: currentCurrency,
    info: currencyInfo
  };

  const date = {
    begin: beginDate,
    end: endDate,
    timezone
  };

  const filters = {
    ...(!isEmpty(cartography) && { cartography }),
    ...variableFilter
  };

  const opts = {
    includeCustomKpis: hasCustomEvents,
    includeFADwellTimeKpis: hasIncludeFADwellTimeKpis,
    minimumViewabilityStats: hasMinimumViewabilityStats
  };

  const handleColumnVisibilityChange = (tableType: string, column: string, visible: boolean) => {
    const storage = localStorage.getItem('campaignStats') ? JSON.parse(localStorage.getItem('campaignStats')!) : null;
    if (storage) {
      const columnTableIndex = storage.findIndex((i: any) => i[`${tableType}VisibleHeaders`]);
      const columnTableVisible = storage[columnTableIndex]?.[`${tableType}VisibleHeaders`];
      const defaultVisibleColumns = statsTableColumns[tableType as TableType].reduce<string[]>(
        (prev, current) => (current.visible ? [...prev, current.prop] : prev),
        [] as string[]
      );
      const userColumnTableVisible = (columnTableVisible?.[session.userFrom || session.user || user._id!] ||
        defaultVisibleColumns) as string[];
      const newUserColumnTableVisible = visible
        ? [...userColumnTableVisible, column]
        : userColumnTableVisible.filter(c => c !== column);
      if (columnTableIndex < 0) {
        const columnTableData = {
          [`${tableType}VisibleHeaders`]: {
            [session.userFrom || session.user || user._id!]: newUserColumnTableVisible
          }
        };
        storage.push(columnTableData);
      } else {
        storage[columnTableIndex][`${tableType}VisibleHeaders`][session.userFrom || session.user || user._id!] =
          newUserColumnTableVisible;
      }
      localStorage.setItem('campaignStats', JSON.stringify(storage));
    }
  };

  const handleGroupsChange = (tableType: string, groups: string[]) =>
    dispatch(reportingSetStatsGroups(tableType, groups));

  return (
    <div className="statsTablesSection">
      {sectionsVisibility.reportingStatsTable && (
        <StatsTable
          campaigns={reportingCampaigns}
          country={country}
          currency={currency}
          date={date}
          defaultGroups={reportGroups.stats}
          filters={filters}
          includeDateGrouping
          onColumnVisibilityChange={(...args) => handleColumnVisibilityChange(TABLE_TYPES.stats, ...args)}
          onGroupsChange={groups => handleGroupsChange('stats', groups)}
          opts={opts}
          session={session}
          staticMode={staticMode}
          staticReports={staticReports}
          statsColumns={statsTableColumns.stats}
          type="stats"
          user={user}
          userRoles={userRoles}
        />
      )}
      {sectionsVisibility.reportingFootfallAttributionTable && hasFootfall && (
        <StatsTable
          campaigns={reportingCampaigns}
          country={country}
          currency={currency}
          date={date}
          defaultGroups={footfallAttributionDefaultGroups}
          filters={filters}
          includeDateGrouping
          onColumnVisibilityChange={(...args) => handleColumnVisibilityChange(TABLE_TYPES.footfallAttribution, ...args)}
          onGroupsChange={groups => handleGroupsChange('footfallAttribution', groups)}
          opts={opts}
          session={session}
          staticMode={staticMode}
          staticReports={staticReports}
          statsColumns={statsTableColumns.footfallAttribution}
          type="footfallAttribution"
          user={user}
          userRoles={userRoles}
        />
      )}
    </div>
  );
}

StatsTablesSection.displayName = 'StatsTablesSection';
export default StatsTablesSection;
